import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { homePathForRole } from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import AssignedBriefs from "@/pages/AssignedBriefs";
import MyCreatives from "@/pages/MyCreatives";
import BatchBuilder from "@/pages/BatchBuilder";
import ReadyToLaunch from "@/pages/ReadyToLaunch";
import ClientPortal from "@/pages/ClientPortal";
import AdminSettings from "@/pages/AdminSettings";
import PublicBoard from "@/pages/PublicBoard";
import Layout from "@/components/Layout";
import { Loader2 } from "lucide-react";

const FullLoader = () => (
  <div className="min-h-screen bg-ink-900 flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-brand" />
  </div>
);

// Stable role arrays (defined once, not re-created per render)
const ROLES = {
  adminStrategist: ["admin", "strategist"],
  adminEditor: ["admin", "editor"],
  client: ["client"],
};

const RoleRoute = ({ roles, children, bare }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homePathForRole(user.role)} replace />;
  return bare ? children : <Layout>{children}</Layout>;
};

const LoginGate = () => {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (user) return <Navigate to={homePathForRole(user.role)} replace />;
  return <Login />;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/b/:token" element={<PublicBoard />} />
      <Route path="/" element={<LoginGate />} />
      <Route path="/dashboard" element={<RoleRoute roles={ROLES.adminStrategist}><Dashboard /></RoleRoute>} />
      <Route path="/assigned-briefs" element={<RoleRoute roles={ROLES.adminEditor}><AssignedBriefs /></RoleRoute>} />
      <Route path="/my-creatives" element={<RoleRoute roles={ROLES.adminEditor}><MyCreatives /></RoleRoute>} />
      <Route path="/batch-builder" element={<RoleRoute roles={ROLES.adminStrategist}><BatchBuilder /></RoleRoute>} />
      <Route path="/ready-to-launch" element={<RoleRoute roles={ROLES.adminStrategist}><ReadyToLaunch /></RoleRoute>} />
      <Route path="/settings" element={<RoleRoute roles={ROLES.adminStrategist}><AdminSettings /></RoleRoute>} />
      <Route path="/portal" element={<RoleRoute roles={ROLES.client} bare><ClientPortal /></RoleRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" theme="dark" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
